import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Bell, Check, Target } from "@phosphor-icons/react";

const POLL_MS = 30000;

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(new Set());
  const primed = useRef(false);
  const panelRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      const unreadItems = data.filter((n) => !n.read);
      setUnread(unreadItems.length);
      // Toast for newly-arrived unread items (skip first-load flood)
      if (primed.current) {
        for (const n of unreadItems) {
          if (!seenIds.current.has(n.id)) {
            toast.success(n.title || "New notification", { description: n.message });
          }
        }
      }
      data.forEach((n) => seenIds.current.add(n.id));
      primed.current = true;
    } catch (err) { /* silently ignore notif fetch errors */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (err) { /* silently ignore notif mark-read errors */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) { /* silently ignore notif mark-all-read errors */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        className="relative border border-[#E5E7EB] p-2 hover:bg-[#F9FAFB]"
        title="Notifications"
      >
        <Bell size={18} weight={unread ? "fill" : "regular"} color={unread ? "#106109" : "#374151"} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center rounded-full"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[90vw] max-w-sm bg-white border border-[#E5E7EB] shadow-xl z-50"
          data-testid="notif-panel"
        >
          <div className="border-b border-[#E5E7EB] px-4 py-3 flex items-center justify-between">
            <div>
              <div className="label-caps">Notifications</div>
              <div className="text-xs text-[#6B7280]">{unread} unread</div>
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="notif-mark-all"
                      className="text-xs text-[#106109] hover:underline inline-flex items-center gap-1">
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">
                <Bell size={20} weight="thin" className="mx-auto mb-2 opacity-60" />
                You're all caught up.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left border-b border-[#F3F4F6] px-4 py-3 flex gap-3 items-start transition-colors duration-200 ${n.read ? "opacity-70" : "bg-[#F0F7EF]"} hover:bg-[#F9FAFB]`}
              >
                <div className="mt-0.5">
                  <Target size={16} weight="fill" color="#106109" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#111827] flex items-center justify-between gap-2">
                    <span>{n.title}</span>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] shrink-0" />}
                  </div>
                  <div className="text-xs text-[#374151] mt-1">{n.message}</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1">{formatWhen(n.created_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
